import type { Breadcrumb, ErrorEvent } from '@sentry/react';

// The login hint token (utils/loginHintToken.ts) is blanked from the URL before
// Sentry.init runs, so the SDK should never see one. These hooks are the second
// line: a token that reappears in a URL — a link rendered into a breadcrumb, a
// navigation the SDK reconstructs from location.href — must not leave the
// browser, because Sentry is a third-party processor and the token identifies a
// patient account for 14 days.

const REDACTED = '[redacted]';

// `lt=` only where it is a real parameter: start of string, or after ? # &.
// Stops at the next delimiter so the rest of the URL survives intact.
const HINT_TOKEN_PATTERN = /(^|[?#&])lt=[^&#\s]*/gi;

const MAX_SCRUB_DEPTH = 4;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

/** Replaces any `lt=<token>` in a string with a redaction marker. */
export const scrubLoginHintToken = (value: string): string =>
  value.replace(HINT_TOKEN_PATTERN, `$1lt=${REDACTED}`);

/**
 * Walks a Sentry payload (breadcrumb data, extra, contexts) scrubbing every
 * string. Arrays and plain objects are rebuilt; anything else is passed through
 * so class instances are not flattened on the way to the wire.
 */
const scrubPayload = (value: unknown, depth = 0): unknown => {
  if (typeof value === 'string') return scrubLoginHintToken(value);
  if (depth >= MAX_SCRUB_DEPTH) return value;
  if (Array.isArray(value)) return value.map((item) => scrubPayload(item, depth + 1));
  if (!isPlainObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, scrubPayload(item, depth + 1)]),
  );
};

/** Sentry `beforeBreadcrumb` hook — navigation crumbs carry the full href. */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const scrubbed: Breadcrumb = { ...breadcrumb };

  if (typeof scrubbed.message === 'string') {
    scrubbed.message = scrubLoginHintToken(scrubbed.message);
  }
  if (scrubbed.data) {
    scrubbed.data = scrubPayload(scrubbed.data) as Breadcrumb['data'];
  }

  return scrubbed;
};

/** Sentry `beforeSend` hook — httpContextIntegration stamps location.href on every event. */
export const scrubSentryEvent = (event: ErrorEvent): ErrorEvent => {
  if (event.request?.url) {
    event.request.url = scrubLoginHintToken(event.request.url);
  }
  if (typeof event.message === 'string') {
    event.message = scrubLoginHintToken(event.message);
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }
  if (event.extra) {
    event.extra = scrubPayload(event.extra) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = scrubPayload(event.contexts) as typeof event.contexts;
  }

  return event;
};
